import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useWorkspacesList } from "@api/workspace.api";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { MainInterface } from "@components/mainInterface/MainInterface";
import { getAccessToken } from "@lib/auth0";
import { AgentAccessTargetPicker } from "../authorize/AgentAccessTargetPicker";
import { AuthorizeHeader } from "../authorize/AuthorizeHeader";
import { PermissionsToggle } from "../authorize/PermissionsToggle";
import {
    getClientDisplayName,
    getClientInstitution,
} from "../authorize/clientIcons";
import {
    AgentAccessTargetFormValues,
    buildAgentAccessGrantTarget,
    getAgentAccessTargetValidationMessage,
} from "../authorize/agentAccessTarget.helpers";
import styles from "../authorize/AuthorizePage.module.scss";

function isRedirectToExpectedUri(options: {
    redirectTo: string;
    expectedRedirectUri: string | null;
}): boolean {
    if (!options.expectedRedirectUri) {
        return false;
    }

    let redirectUrl: URL;
    let expectedUrl: URL;
    try {
        redirectUrl = new URL(options.redirectTo);
        expectedUrl = new URL(options.expectedRedirectUri);
    } catch {
        return false;
    }

    if (
        redirectUrl.protocol !== expectedUrl.protocol ||
        redirectUrl.username !== expectedUrl.username ||
        redirectUrl.password !== expectedUrl.password ||
        redirectUrl.host !== expectedUrl.host ||
        redirectUrl.pathname !== expectedUrl.pathname
    ) {
        return false;
    }

    for (const [key, value] of expectedUrl.searchParams.entries()) {
        if (redirectUrl.searchParams.get(key) !== value) {
            return false;
        }
    }

    return true;
}

function getRedirectTo(
    payload: object,
    expectedRedirectUri: string | null,
): string | null {
    if ("redirectTo" in payload && typeof payload.redirectTo === "string") {
        if (
            !isRedirectToExpectedUri({
                redirectTo: payload.redirectTo,
                expectedRedirectUri,
            })
        ) {
            return null;
        }
        return payload.redirectTo;
    }
    return null;
}

function haveSameParamValues(
    leftParams: URLSearchParams,
    rightParams: URLSearchParams,
    key: string,
): boolean {
    const leftValues = leftParams.getAll(key);
    const rightValues = rightParams.getAll(key);
    return (
        leftValues.length === rightValues.length &&
        leftValues.every((value, index) => value === rightValues[index])
    );
}

function isSameRedirectBase(expected: URL, actual: URL): boolean {
    return (
        expected.protocol === actual.protocol &&
        expected.username === actual.username &&
        expected.password === actual.password &&
        expected.hostname === actual.hostname &&
        expected.port === actual.port &&
        expected.pathname === actual.pathname
    );
}

function isValidRedirectTo(options: {
    redirectTo: string;
    requestedRedirectUri: string | null;
    requestedState: string | null;
}): boolean {
    if (!options.requestedRedirectUri) {
        return false;
    }

    let requested: URL;
    let actual: URL;
    try {
        requested = new URL(options.requestedRedirectUri);
        actual = new URL(options.redirectTo);
    } catch {
        return false;
    }

    if (!isSameRedirectBase(requested, actual)) {
        return false;
    }

    if (!actual.searchParams.get("code")) {
        return false;
    }

    if (actual.searchParams.get("state") !== options.requestedState) {
        return false;
    }

    for (const key of actual.searchParams.keys()) {
        if (
            key !== "code" &&
            key !== "state" &&
            !requested.searchParams.has(key)
        ) {
            return false;
        }
    }

    for (const key of requested.searchParams.keys()) {
        if (
            key !== "code" &&
            key !== "state" &&
            !haveSameParamValues(
                requested.searchParams,
                actual.searchParams,
                key,
            )
        ) {
            return false;
        }
    }

    return true;
}

function paramsToBody(searchParams: URLSearchParams): Record<string, string> {
    const body: Record<string, string> = {};
    searchParams.forEach((value, key) => {
        body[key] = value;
    });
    return body;
}

function getInitialTargetValues(
    workspaceId: string | null,
): AgentAccessTargetFormValues {
    return {
        targetType: workspaceId ? "workspaces" : "all",
        organizationIds: [],
        workspaceIds: workspaceId ? [workspaceId] : [],
    };
}

const McpAuthorizeContent: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState<"idle" | "approved" | "error">("idle");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { data: workspaces } = useWorkspacesList({ includeEntities: false });

    const clientId = searchParams.get("client_id");
    const clientName = searchParams.get("client_name");
    const redirectUri = searchParams.get("redirect_uri");
    const workspaceId = searchParams.get("workspace_id");
    const scope = searchParams.get("scope") ?? "mcp:read mcp:write";
    const requestedScopes = useMemo(
        () => scope.split(" ").filter(Boolean),
        [scope],
    );
    const canWrite = requestedScopes.includes("mcp:write");
    const [scopeChoice, setScopeChoice] = useState<"read" | "write">(
        canWrite ? "write" : "read",
    );
    const [targetValues, setTargetValues] =
        useState<AgentAccessTargetFormValues>(() =>
            getInitialTargetValues(workspaceId),
        );
    const hasRequiredParams = Boolean(
        clientId &&
        redirectUri &&
        searchParams.get("code_challenge") &&
        searchParams.get("code_challenge_method") === "S256",
    );
    const targetValidationMessage =
        getAgentAccessTargetValidationMessage(targetValues);
    const clientInstitution = getClientInstitution(clientName);
    const clientDisplayName = getClientDisplayName(clientName);

    const handleAuthorize = useCallback(async () => {
        if (targetValidationMessage) {
            return;
        }

        setIsSubmitting(true);
        setStatus("idle");
        try {
            const token = await getAccessToken();
            if (!token) {
                throw new Error("Missing access token");
            }

            const body = {
                ...paramsToBody(searchParams),
                selected_scope:
                    scopeChoice === "read" ? "mcp:read" : "mcp:read mcp:write",
                grantTarget: buildAgentAccessGrantTarget(targetValues),
            };

            const response = await fetch("/mcp/oauth/authorize", {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                throw new Error(`Authorization failed (${response.status})`);
            }
            const payload = await response.json();
            if (!payload || typeof payload !== "object") {
                throw new Error("Invalid authorization response");
            }
            const redirectTo = getRedirectTo(payload, redirectUri);
            if (!redirectTo) {
                throw new Error("Missing redirect target");
            }
            if (
                !isValidRedirectTo({
                    redirectTo,
                    requestedRedirectUri: redirectUri,
                    requestedState: searchParams.get("state"),
                })
            ) {
                throw new Error("Invalid redirect target");
            }
            setStatus("approved");
            globalThis.location.assign(redirectTo);
        } catch {
            setStatus("error");
        } finally {
            setIsSubmitting(false);
        }
    }, [
        redirectUri,
        scopeChoice,
        searchParams,
        targetValidationMessage,
        targetValues,
    ]);

    if (!hasRequiredParams) {
        return (
            <div className={styles.card}>
                <div className={styles.emptyStateWrap}>
                    <EmptyState
                        header="Invalid MCP authorization request"
                        body="The MCP client did not send the required OAuth and PKCE parameters. Retry from your MCP client."
                    />
                </div>
            </div>
        );
    }

    if (status === "approved") {
        return (
            <div className={styles.card}>
                <div className={styles.emptyStateWrap}>
                    <EmptyState
                        header="Authorization complete"
                        body="Returning to your client."
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.card}>
            <AuthorizeHeader
                leftIcon={
                    <InstitutionIcon
                        institution={clientInstitution}
                        className={styles.headerClientIcon}
                    />
                }
                title={`${clientDisplayName} is requesting access`}
            />
            <section className={styles.section}>
                <h4 className={styles.sectionHeading}>Permissions</h4>
                <PermissionsToggle
                    value={scopeChoice}
                    onChange={setScopeChoice}
                    canWrite={canWrite}
                />
            </section>

            <AgentAccessTargetPicker
                values={targetValues}
                workspaces={workspaces ?? []}
                onChange={setTargetValues}
                errorMessage={targetValidationMessage}
            />

            {status === "error" && (
                <div className={styles.fieldError}>
                    Authorization failed. Refresh and try again, or restart from
                    your MCP client.
                </div>
            )}

            <footer className={styles.footer}>
                <ButtonWithLoader
                    variant="primary"
                    size="xl"
                    loading={isSubmitting}
                    onClick={handleAuthorize}
                    disabled={Boolean(targetValidationMessage) || isSubmitting}
                    hotKey="mod+enter"
                    hotKeyOptions={{
                        description: "Authorize MCP access",
                        enableOnFormTags: true,
                        enabled: !isSubmitting && !targetValidationMessage,
                    }}
                    hotKeyDisplayPosition="inline"
                >
                    Authorize
                </ButtonWithLoader>
            </footer>
        </div>
    );
};

export const McpAuthorizePage: React.FC = () => (
    <MainInterface sideNavigation={null}>
        <div className={styles.page}>
            <div className={styles.content}>
                <McpAuthorizeContent />
            </div>
        </div>
    </MainInterface>
);
